import React, { useCallback, useEffect, useState } from 'react';
import { fetchRequestsData } from '../../services/dataApi';
import PageLoader from '../../components/PageLoader';
import RequestsTable from '../../components/RequestsTable';
import SearchBar from '../../components/SearchBar';
import useUrlPagination from '../../hooks/useUrlPagination';
import '../Page.css';

const AdminRequests = () => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchRequestsData(currentPage, itemsPerPage, debouncedSearchTerm);
      setData(Array.isArray(result.data) ? result.data : []);
      setPagination(result.pagination || null);
    } catch (err) {
      setError(err.message || 'An error occurred while loading Requests');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Requests</h1>
          <p>Review operational requests received through Corezoid.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Requests</div>
            <div className="overview-value">{pagination?.total ?? data.length}</div>
            <div className="overview-meta">All records</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Scope</div>
            <div className="overview-value">Global</div>
            <div className="overview-meta">Admin access</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          <div className="page-data">
            <div className="page-section-header">
              <div className="page-section-title">
                <h2>Request Feed</h2>
                <p>Search across all fields in the current result page.</p>
              </div>
              <div className="page-section-meta"><div className="page-chip">Admin</div></div>
            </div>
            <SearchBar
              onSearch={handleSearch}
              value={searchTerm}
              resultCount={pagination?.total || 0}
              isUpdating={searchTerm !== debouncedSearchTerm}
              placeholder="Search requests..."
            />
            <RequestsTable
              data={data}
              pagination={pagination}
              onPageChange={setCurrentPage}
            />
          </div>
        </PageLoader>
      </div>
    </div>
  );
};

export default AdminRequests;
